import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { CheckCircle, XCircle, Clock, DollarSign, Package } from 'lucide-react';
import { toast } from 'sonner';
import FOCRequests from './FOCRequests';

const API = process.env.REACT_APP_BACKEND_URL || '';

const ApprovalsManagement = () => {
  const [feeRequests, setFeeRequests] = useState([]);
  const [kitRequests, setKitRequests] = useState([]);
  const [pendingCount, setPendingCount] = useState({});
  const [reviewDialog, setReviewDialog] = useState({ open: false, type: null, item: null });
  const [reviewData, setReviewData] = useState({ status: 'approved', notes: '' });

  useEffect(() => {
    loadAllRequests();
    loadPendingCount();
  }, []);

  const loadPendingCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/approvals/pending-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPendingCount(response.data);
    } catch (error) {
      console.error('Error loading pending count:', error);
    }
  };

  const loadAllRequests = async () => {
    await Promise.all([loadFeeRequests(), loadKitRequests()]);
  };

  const loadFeeRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/fee-structure-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeeRequests(response.data);
    } catch (error) {
      console.error('Error loading fee requests:', error);
    }
  };

  const loadKitRequests = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/kit-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setKitRequests(response.data);
    } catch (error) {
      console.error('Error loading kit requests:', error);
    }
  };

  const openReviewDialog = (type, item) => {
    setReviewDialog({ open: true, type, item });
    setReviewData({ status: 'approved', notes: '' });
  };

  const handleReview = async () => {
    try {
      const token = localStorage.getItem('token');
      
      if (reviewDialog.type === 'fee') {
        await axios.put(
          `${API}/api/fee-structure-requests/${reviewDialog.item.id}/review`,
          { status: reviewData.status, admin_notes: reviewData.notes },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Fee request ${reviewData.status}`);
      } else if (reviewDialog.type === 'kit') {
        await axios.put(
          `${API}/api/kit-requests/${reviewDialog.item.id}`,
          { status: reviewData.status, admin_notes: reviewData.notes },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success(`Kit request ${reviewData.status}`);
      }

      setReviewDialog({ open: false, type: null, item: null });
      loadAllRequests();
      loadPendingCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to review request');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle },
      in_progress: { color: 'bg-blue-100 text-blue-800', icon: Clock },
      in_transit: { color: 'bg-purple-100 text-purple-800', icon: Package },
      delivered: { color: 'bg-green-100 text-green-800', icon: CheckCircle }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`px-2 py-1 ${badge.color} text-xs rounded flex items-center gap-1 w-fit`}>
        <Icon className="w-3 h-3" />
        {status.toUpperCase().replace('_', ' ')}
      </span>
    );
  };

  const pendingFeeRequests = feeRequests.filter(r => r.status === 'pending');
  const pendingKitRequests = kitRequests.filter(r => r.status === 'pending');

  return (
    <div className="space-y-6">
      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6 bg-gradient-to-br from-orange-50 to-amber-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Total Pending</p>
              <h3 className="text-3xl font-bold text-orange-700">{pendingCount.total_pending || 0}</h3>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-blue-50 to-cyan-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Fee Requests</p>
              <h3 className="text-3xl font-bold text-blue-700">{pendingCount.fee_structure_requests || 0}</h3>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </Card>

        <Card className="p-6 bg-gradient-to-br from-purple-50 to-pink-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600 mb-1">Kit Requests</p>
              <h3 className="text-3xl font-bold text-purple-700">{pendingCount.kit_requests || 0}</h3>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Package className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </Card>
      </div>

      {/* Tabs for Different Request Types */}
      <Card className="p-6">
        <h3 className="text-lg font-semibold mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          Pending Approvals
        </h3>

        <Tabs defaultValue="foc" className="w-full">
          <TabsList>
            <TabsTrigger value="foc">
              FOC Requests
            </TabsTrigger>
            <TabsTrigger value="fee">
              Fee Structure Requests ({pendingFeeRequests.length})
            </TabsTrigger>
            <TabsTrigger value="kit">
              Kit Requests ({pendingKitRequests.length})
            </TabsTrigger>
          </TabsList>

          {/* Fee Structure Requests */}
          <TabsContent value="fee" className="space-y-4 mt-4">
            {pendingFeeRequests.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No pending fee structure requests</p>
            ) : (
              pendingFeeRequests.map((request) => (
                <Card key={request.id} className="p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold">{request.center_name || 'Unknown Center'}</h4>
                        {getStatusBadge(request.status)}
                      </div>
                      <p className="text-sm text-gray-600">
                        Requested by: {request.teacher_name} ({request.teacher_email})
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(request.created_at).toLocaleDateString()} at {new Date(request.created_at).toLocaleTimeString()}
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-3 text-sm border-t pt-3">
                    <div>
                      <p className="text-xs text-gray-500 font-medium mb-1">ONLINE FEES</p>
                      <p>Monthly: ₹{request.online_monthly_fee}</p>
                      {request.online_quarterly_fee && <p>Quarterly: ₹{request.online_quarterly_fee}</p>}
                      {request.online_yearly_fee && <p>Yearly: ₹{request.online_yearly_fee}</p>}
                    </div>
                    <div>
                      <p className="text-xs text-gray-500 font-medium mb-1">OFFLINE FEES</p>
                      <p>Monthly: ₹{request.offline_monthly_fee}</p>
                      {request.offline_quarterly_fee && <p>Quarterly: ₹{request.offline_quarterly_fee}</p>}
                      {request.offline_yearly_fee && <p>Yearly: ₹{request.offline_yearly_fee}</p>}
                    </div>
                  </div>

                  <div className="text-sm mb-3 bg-gray-50 p-3 rounded">
                    <p className="font-medium text-gray-700 mb-1">Reason:</p>
                    <p className="text-gray-600">{request.reason || 'No reason provided'}</p>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => openReviewDialog('fee', request)}
                      size="sm"
                      className="flex-1"
                    >
                      Review Request
                    </Button>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Kit Requests */}
          <TabsContent value="kit" className="space-y-4 mt-4">
            {pendingKitRequests.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No pending kit requests</p>
            ) : (
              pendingKitRequests.map((request) => (
                <Card key={request.id} className="p-4 hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h4 className="font-semibold">Student: {request.student_name || 'Unknown'}</h4>
                        {getStatusBadge(request.status)}
                      </div>
                      <p className="text-sm text-gray-600">Requested by: {request.teacher_name}</p>
                      <p className="text-xs text-gray-500">
                        {new Date(request.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  {request.teacher_notes && (
                    <div className="text-sm mb-3 bg-gray-50 p-3 rounded">
                      <p className="font-medium text-gray-700 mb-1">Notes:</p>
                      <p className="text-gray-600">{request.teacher_notes}</p>
                    </div>
                  )}

                  <div className="flex gap-2">
                    <Button
                      onClick={() => openReviewDialog('kit', request)}
                      size="sm"
                      className="flex-1"
                    >
                      Review Request
                    </Button>
                  </div>
                </Card>
              ))
            )}
          </TabsContent>

          {/* FOC Requests */}
          <TabsContent value="foc">
            <FOCRequests />
          </TabsContent>
        </Tabs>
      </Card>

      {/* Review Dialog */}
      <Dialog open={reviewDialog.open} onOpenChange={(open) => setReviewDialog({ ...reviewDialog, open })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Review {reviewDialog.type === 'fee' ? 'Fee Structure' : 'Kit'} Request</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label htmlFor="status">Decision *</Label>
              <select
                id="status"
                value={reviewData.status}
                onChange={(e) => setReviewData({ ...reviewData, status: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
              >
                <option value="approved">Approve</option>
                <option value="rejected">Reject</option>
                {reviewDialog.type === 'kit' && (
                  <>
                    <option value="in_progress">In Progress</option>
                    <option value="in_transit">In Transit</option>
                    <option value="delivered">Delivered</option>
                  </>
                )}
              </select>
            </div>

            <div>
              <Label htmlFor="notes">Admin Notes</Label>
              <textarea
                id="notes"
                value={reviewData.notes}
                onChange={(e) => setReviewData({ ...reviewData, notes: e.target.value })}
                className="w-full px-3 py-2 border rounded-md"
                rows="3"
                placeholder="Add any notes for the teacher..."
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleReview} className="flex-1">
                Submit Review
              </Button>
              <Button
                variant="outline"
                onClick={() => setReviewDialog({ open: false, type: null, item: null })}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApprovalsManagement;
